import { Component } from '@theme/component';
import {
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} closeButton - The close button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'closeButton'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #activeIndex = 0;

  /** @type {boolean} */
  #wheelNavigationLocked = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #wheelNavigationTimeout;

  /** @type {number} */
  #wheelDelta = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('resize', this.layoutActiveMedia);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('resize', this.layoutActiveMedia);
    clearTimeout(this.#wheelNavigationTimeout);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();
    const mediaGallery = this.closest('media-gallery');
    // A carousel can be moved with its controls or swipe gestures, leaving the
    // click handler's rendered index out of date. Open the slide that is
    // currently selected in that carousel instead.
    if (mediaGallery?.presentation === 'carousel' && Number.isInteger(mediaGallery.slideshow?.current)) {
      index = mediaGallery.slideshow.current;
    }
    index = Number(index);
    if (!Number.isInteger(index) || index < 0 || index >= this.refs.media.length) return;
    clearTimeout(this.#wheelNavigationTimeout);
    this.#activeIndex = index;
    this.#wheelDelta = 0;
    this.#wheelNavigationLocked = false;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // Apply the requested item after opening as well. The native dialog can
      // recalculate layout while opening, so doing this only before showModal()
      // can leave a stale item marked active for the first wheel event.
      this.setActiveMedia(index);
      this.setActiveThumbnail(index);

      targetThumbnail?.scrollIntoView({ behavior: 'instant' });
      if (window.innerWidth < 750) targetImage?.scrollIntoView({ behavior: 'instant' });

      this.layoutActiveMedia();
    };

    open();
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (window.innerWidth >= 750) return;
    if (this.#wheelNavigationLocked) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.setActiveThumbnail(activeIndex);

    this.#activeIndex = activeIndex;
    this.setActiveMedia(activeIndex);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Advances one media item for each vertical wheel gesture.
   * Trackpad momentum is briefly locked so it cannot skip multiple images.
   *
   * @param {WheelEvent} event - The wheel event from the open dialog.
   */
  handleWheel = (event) => {
    if (!this.refs.dialog.open || event.ctrlKey) return;
    if (event.target instanceof Element && event.target.closest('.dialog-thumbnails-list')) return;

    event.preventDefault();
    // Wheel and trackpad gestures send a burst of events (including momentum
    // events after the fingers leave the trackpad). Keep extending the lock
    // until that burst has stopped, so one gesture can only select one image.
    clearTimeout(this.#wheelNavigationTimeout);
    this.#wheelNavigationTimeout = setTimeout(() => {
      this.#wheelNavigationLocked = false;
      this.#wheelDelta = 0;
      if (window.innerWidth < 750) this.handleScroll();
    }, 180);

    if (this.#wheelNavigationLocked) return;

    this.#wheelDelta += event.deltaY;
    if (Math.abs(this.#wheelDelta) < 12) return;

    const activeIndex = this.getActiveMediaIndex();
    const lastIndex = this.refs.media.length - 1;
    const targetIndex = Math.min(lastIndex, Math.max(0, activeIndex + Math.sign(this.#wheelDelta)));
    this.#wheelDelta = 0;
    if (targetIndex === activeIndex) return;

    this.#wheelNavigationLocked = true;
    this.#activeIndex = targetIndex;

    const behavior = window.innerWidth >= 750 || prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(targetIndex, { behavior });
  };

  /**
   * Positions the close button just outside the active image's top-right corner.
   */
  positionCloseButton = () => {
    const { closeButton, dialog, media } = this.refs;
    if (!dialog.open) return;

    requestAnimationFrame(() => {
      const activeMedia = media[this.#activeIndex];
      const image = activeMedia?.querySelector('.product-media__image');
      const productMedia = activeMedia?.querySelector('.product-media');
      const anchor = image instanceof HTMLElement ? image : activeMedia;
      if (!anchor) return;

      const imageBounds = this.getVisibleImageBounds(anchor, productMedia);
      const buttonBounds = closeButton.getBoundingClientRect();
      const gap = 8;
      const inset = 12;
      const isMobile = window.innerWidth < 750;
      const left = isMobile
        ? imageBounds.right - buttonBounds.width - gap
        : Math.min(window.innerWidth - buttonBounds.width - inset, imageBounds.right + gap);
      const top = isMobile
        ? imageBounds.top + gap
        : Math.max(inset, imageBounds.top - buttonBounds.height - gap);

      closeButton.style.left = `${Math.max(inset, left)}px`;
      closeButton.style.top = `${top}px`;
      closeButton.style.right = 'auto';
      closeButton.style.visibility = 'visible';
    });
  };

  /**
   * Gets the bounds of the visible image pixels when an image uses object-fit:
   * contain. On mobile the img element fills the viewport, so its DOM bounds
   * alone do not identify where the image itself begins vertically.
   *
   * @param {HTMLElement} image - The image element or its fallback container.
   * @param {Element | null | undefined} productMedia - The containing product media element.
   */
  getVisibleImageBounds(image, productMedia) {
    const bounds = image.getBoundingClientRect();
    const ratio = image instanceof HTMLImageElement && image.naturalWidth && image.naturalHeight
      ? image.naturalWidth / image.naturalHeight
      : Number.parseFloat(productMedia ? getComputedStyle(productMedia).getPropertyValue('--ratio') : '');

    if (!Number.isFinite(ratio) || ratio <= 0 || !bounds.width || !bounds.height) return bounds;

    const frameRatio = bounds.width / bounds.height;
    if (ratio >= frameRatio) {
      const height = bounds.width / ratio;
      const top = bounds.top + (bounds.height - height) / 2;
      return { left: bounds.left, right: bounds.right, top, bottom: top + height };
    }

    const width = bounds.height * ratio;
    const left = bounds.left + (bounds.width - width) / 2;
    return { left, right: left + width, top: bounds.top, bottom: bounds.bottom };
  }

  /**
   * Sizes the active image from its media ratio.
   * This keeps lazy-loaded images from opening at a different size than the first image.
   */
  sizeActiveMedia() {
    const activeMedia = this.refs.media[this.#activeIndex];
    const productMedia = activeMedia?.querySelector('.product-media');
    if (!(productMedia instanceof HTMLElement)) return;

    const ratio = Number.parseFloat(getComputedStyle(productMedia).getPropertyValue('--ratio'));
    if (!Number.isFinite(ratio) || ratio <= 0) return;

    const maxWidth = Math.min(window.innerWidth * 0.72, 1100);
    const maxHeight = Math.min(window.innerHeight * 0.78, 850);
    const width = Math.min(maxWidth, maxHeight * ratio);

    productMedia.style.setProperty('--zoom-media-width', `${width}px`);
    productMedia.style.setProperty('--zoom-media-height', `${width / ratio}px`);
  }

  /**
   * Updates the active media layout after the viewport changes.
   */
  layoutActiveMedia = () => {
    this.sizeActiveMedia();
    this.positionCloseButton();
    this.positionThumbnails();
  };

  /**
   * Places the mobile thumbnail rail immediately beneath the visible image.
   * Wide images leave space below their contained pixels; tall images fall
   * back to the lowest position that keeps the rail within the viewport.
   */
  positionThumbnails = () => {
    if (window.innerWidth >= 750) return;

    const container = this.refs.thumbnails.closest('.dialog-thumbnails-list-container');
    const activeMedia = this.refs.media[this.#activeIndex];
    const image = activeMedia?.querySelector('.product-media__image');
    const productMedia = activeMedia?.querySelector('.product-media');
    if (!(container instanceof HTMLElement) || !(image instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const imageBounds = this.getVisibleImageBounds(image, productMedia);
      const inset = 12;
      const top = Math.min(imageBounds.bottom + 8, window.innerHeight - container.offsetHeight - inset);

      container.style.top = `${Math.max(inset, top)}px`;
      container.style.bottom = 'auto';
    });
  };

  /**
   * Displays one zoomed media item at a time on desktop.
   * Mobile retains its native horizontal swipe behavior.
   *
   * @param {number} index - The media item to display.
   */
  setActiveMedia(index) {
    if (window.innerWidth < 750) return;

    this.refs.media.forEach((media, mediaIndex) => {
      const isActive = mediaIndex === index;
      media.classList.toggle('is-active', isActive);
      media.setAttribute('aria-hidden', `${!isActive}`);
    });

    this.sizeActiveMedia();
  }

  /**
   * Marks the thumbnail that represents the displayed zoom image.
   *
   * @param {number} index - The selected media item.
   */
  setActiveThumbnail(index) {
    Array.from(this.refs.thumbnails.querySelectorAll('button')).forEach((button, buttonIndex) => {
      button.setAttribute('aria-selected', `${buttonIndex === index}`);
    });
  }

  /**
   * Gets the visible slide index on desktop, falling back to the current state on mobile.
   *
   * @returns {number}
   */
  getActiveMediaIndex() {
    return Number(this.#activeIndex);
  }

  /**
   * Closes the zoom dialog.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.setActiveThumbnail(index);

    this.#activeIndex = index;
    this.setActiveMedia(index);
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage && window.innerWidth < 750) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
